import React, { useId, useRef, useState } from "react";
import { Modal } from "../ui/Modal";
import { useTranslation } from "react-i18next";
import { ExclamationCircleFill } from "../icons";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import "../../styles/configurable-options-modal.scss";
import PrimaryButton from "../buttons/primaryButton";
import { useInFlight } from "../hooks/useInFlight";

export interface OptionValue {
  id?: string;
  name?: string;
  label?: string;
  price_change?: number;
  selected?: boolean;
}

export interface Option {
  id?: string;
  name?: string;
  label?: string;
  description?: string;
  min?: number;
  max?: number;
  values?: OptionValue[];
}

export const hasMinMax = (option: Option) =>
  (option.min !== undefined && option.min > 0) || (option.max !== undefined && option.max > 0);

type InstructionTranslator = (key: string, fallback: string, params?: Record<string, number>) => string;

const getPositiveLimit = (limit?: number) => {
  if (limit !== undefined && limit > 0) {
    return limit;
  }
  return undefined;
};

const translateSelectionCount = (
  count: number,
  singularKey: string,
  pluralKey: string,
  singularFallback: string,
  pluralFallback: string,
  parameterName: "count" | "min" | "max",
  t: InstructionTranslator,
) => {
  if (count === 1) {
    return t(singularKey, singularFallback, { [parameterName]: count });
  }
  return t(pluralKey, pluralFallback, { [parameterName]: count });
};

export const getSelectionInstructionText = (option: Option, t: InstructionTranslator) => {
  const min = getPositiveLimit(option.min);
  const max = getPositiveLimit(option.max);
  if (min !== undefined && max !== undefined) {
    if (min === max) {
      return translateSelectionCount(
        min,
        "required_select_exact_option",
        "required_select_exact_options",
        "Required. Select exactly {{count}} option.",
        "Required. Select exactly {{count}} options.",
        "count",
        t,
      );
    }
    return t("required_select_between_options", "Required. Select between {{min}} and {{max}} options.", {
      min,
      max,
    });
  }
  if (min !== undefined) {
    return translateSelectionCount(
      min,
      "required_select_at_least_option",
      "required_select_at_least_options",
      "Required. Select at least {{min}} option.",
      "Required. Select at least {{min}} options.",
      "min",
      t,
    );
  }
  if (max !== undefined) {
    return translateSelectionCount(
      max,
      "optional_select_up_to_option",
      "optional_select_up_to_options",
      "Select up to {{max}} option. (Optional)",
      "Select up to {{max}} options. (Optional)",
      "max",
      t,
    );
  }
  return t("optional_select_any_options", "Optional. Select any that apply.");
};

export const getCounterDisplay = (option: Option) => {
  const hasMin = option.min !== undefined && option.min > 0;
  const hasMax = option.max !== undefined && option.max > 0;
  if (hasMin && hasMax) {
    return option.min === option.max ? option.min : `${option.min}-${option.max}`;
  }
  return hasMin ? option.min : option.max;
};

export const getMinmaxClassName = (base: string, hasError: boolean) => (hasError ? `${base} ${base}--error` : base);

interface ConfigurableOptionsModalProps {
  show: boolean;
  options: Option[];
  selectedOptions: { [optionKey: string]: { [valueKey: string]: boolean } };
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  getOptionKey: (option: Option, idx: number) => string | number;
  getValueKey: (value: OptionValue, vIdx: number) => string | number;
  getOptionMinMaxText: (option: Option) => string | null;
  handleOptionValueChange: (optionKey: string, valueKey: string) => void;
  itemName: string;
  currency: string;
  countryCode: string;
  hidePrices?: boolean;
}

const ConfigurableOptionsModal: React.FC<ConfigurableOptionsModalProps> = React.memo(
  ({
    show,
    options,
    selectedOptions,
    onClose,
    onConfirm,
    getOptionKey,
    getValueKey,
    getOptionMinMaxText,
    handleOptionValueChange,
    itemName,
    currency,
    countryCode,
    hidePrices = false,
  }) => {
    const { t } = useTranslation();
    const idPrefix = useId();
    const optionSectionRefs = useRef<Array<HTMLFieldSetElement | null>>([]);
    const [hasAttemptedConfirm, setHasAttemptedConfirm] = useState(false);
    const { isPending, run } = useInFlight();

    // Validation: for each option, check if min/max are satisfied
    const optionValidity = options.map((option, idx) => {
      const optionKey = getOptionKey(option, idx);
      const selectedCount = option.values
        ? option.values.reduce((acc, value, vIdx) => {
            const valueKey = getValueKey(value, vIdx);
            return acc + (selectedOptions[optionKey]?.[valueKey] ? 1 : 0);
          }, 0)
        : 0;
      const min = option.min ?? 0;
      const max = option.max ?? undefined;
      const valid = selectedCount >= min && (max === undefined || selectedCount <= max);
      return { valid, selectedCount, min, max };
    });
    const allValid = optionValidity.every((v) => v.valid);

    const handleConfirmClick = () => {
      if (!allValid) {
        setHasAttemptedConfirm(true);
        const firstInvalidIndex = optionValidity.findIndex((validity) => !validity.valid);
        optionSectionRefs.current[firstInvalidIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      setHasAttemptedConfirm(false);
      void run(async () => {
        await onConfirm();
      });
    };

    const handleClose = () => {
      setHasAttemptedConfirm(false);
      onClose();
    };

    const showErrors = hasAttemptedConfirm && !allValid;

    return (
      <Modal show={show} onHide={handleClose} dialogClassName="configurable-options-modal" centered>
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title className="modal-title-custom">
            {itemName
              ? t("options_for", "Options for {{itemName}}", { itemName })
              : t("configurable_options", "Configurable Options")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {showErrors && (
            <div role="alert" className="options-error-banner widget-error-panel gcw:mb-bs-3">
              {t("please_review_option_selections", "Please review your option selections to proceed")}
            </div>
          )}
          {options.map((option, idx) => {
            const optionKey = getOptionKey(option, idx);
            const validity = optionValidity[idx];
            const showOptionError = showErrors && !validity.valid;
            const instructionId = `${idPrefix}-option-${idx}-instruction`;
            const describedBy = instructionId;
            return (
              <fieldset
                key={optionKey}
                ref={(element) => {
                  optionSectionRefs.current[idx] = element;
                }}
                className="option-section"
                disabled={isPending}
                aria-describedby={describedBy}
                aria-invalid={showOptionError || undefined}
              >
                <legend className="option-title">
                  {option.label ?? option.name ?? t("option_number", "Option {{number}}", { number: idx + 1 })}
                </legend>
                <div id={instructionId} className={getMinmaxClassName("option-minmax-row", showOptionError)}>
                  {showOptionError && (
                    <ExclamationCircleFill className="option-error-icon" size={16} aria-hidden="true" />
                  )}
                  <span className={getMinmaxClassName("option-minmax-text", showOptionError)}>
                    {getSelectionInstructionText(option, t)}
                  </span>
                  {hasMinMax(option) ? (
                    <span className={getMinmaxClassName("option-minmax-counter", showOptionError)}>
                      {validity.selectedCount}/{getCounterDisplay(option)}
                    </span>
                  ) : null}
                </div>
                <ul className="options-list option-values-list">
                  {option.values?.map((value, vIdx) => {
                    const valueKey = getValueKey(value, vIdx);
                    const inputId = `${idPrefix}-option-${idx}-value-${vIdx}`;
                    const checked = !!selectedOptions[optionKey]?.[valueKey];
                    return (
                      <li className="option-value gcw:flex gcw:items-center gcw:justify-between" key={valueKey}>
                        <label htmlFor={inputId} className="option-label">
                          <span className="main-content">
                            {value.name ?? value.label ?? valueKey}
                            {!hidePrices && value.price_change ? (
                              <span className="price-change">
                                +{formatCurrency(value.price_change, currency, countryCode)}
                              </span>
                            ) : null}
                          </span>
                        </label>
                        <input
                          type="checkbox"
                          id={inputId}
                          checked={checked}
                          onChange={() => handleOptionValueChange(String(optionKey), String(valueKey))}
                          className="option-checkbox"
                          aria-busy={isPending || undefined}
                          aria-describedby={describedBy}
                          aria-invalid={showOptionError || undefined}
                        />
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            );
          })}
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <PrimaryButton
            text={t("confirm", "Confirm")}
            onClick={handleConfirmClick}
            className={
              "gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button gcw:w-full"
            }
            disabled={isPending}
            loading={isPending}
          />
        </Modal.Footer>
      </Modal>
    );
  },
);

export default ConfigurableOptionsModal;
